import React, { FC } from 'react';
import { Asset, Button, CloseIcon, Modal } from '@clearcover/atlas';

import styles from './styles.module.css';

export type AutopayModalState = 'loading' | 'increased' | 'no-change';

type AutopayDownpaymentModalProps = {
  open: boolean;
  state: AutopayModalState;
  previousDownpayment: string;
  newDownpayment: string;
  onEnableAutopay: () => void;
  onUnderstand: () => void;
  onContinue: () => void;
};

export const AutopayDownpaymentModal: FC<AutopayDownpaymentModalProps> = ({
  open,
  state,
  previousDownpayment,
  newDownpayment,
  onEnableAutopay,
  onUnderstand,
  onContinue,
}) => {
  const isDismissible = state !== 'loading';

  return (
    <Modal
      controlled
      open={open}
      variant="xl"
      handleClose={isDismissible ? onUnderstand : () => {}}
      showCloseIcon={false}
    >
      {() => (
        <div className={styles.modal} data-testid="autopay-downpayment-modal">
          <div className={styles.header}>
            <h3 className={styles.title}>
              {state === 'loading' &&
                'Your downpayment may be impacted by this change'}
              {state === 'increased' && 'Your down payment has changed'}
              {state === 'no-change' && 'Your downpayment was not affected.'}
            </h3>
            {isDismissible && (
              <button
                type="button"
                data-testid="close-autopay-downpayment-modal"
                onClick={onUnderstand}
              >
                <Asset
                  icon={CloseIcon}
                  description="Close autopay downpayment modal"
                  height={16}
                  width={16}
                />
              </button>
            )}
          </div>

          {state === 'loading' && (
            <div className={styles.content}>
              <p>Please wait while we calculate</p>
            </div>
          )}

          {state === 'increased' && (
            <>
              <div className={styles.content}>
                <p>
                  Turning off autopay has increased the down payment from{' '}
                  <strong>{previousDownpayment}</strong> to{' '}
                  <strong data-testid="new-downpayment-amount">
                    {newDownpayment}
                  </strong>
                  .
                </p>
                <p>
                  Enabling autopay allows for a lower down payment. Would you
                  like to re-enable autopay?
                </p>
              </div>

              <div className={styles.actions}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onUnderstand}
                  data-testid="autopay-modal-understand-button"
                >
                  I Understand
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={onEnableAutopay}
                  data-testid="autopay-modal-enable-button"
                >
                  Enable Autopay
                </Button>
              </div>
            </>
          )}

          {state === 'no-change' && (
            <div className={styles.actions}>
              <Button
                type="button"
                variant="primary"
                onClick={onContinue}
                data-testid="autopay-modal-continue-button"
              >
                Continue
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
